import { postAnalyticsEvents } from "~/api/analytics";
import type { AnalyticsEvent } from "~/types/analytics";
import { devLog, devError } from "./dev-log";

/**
 * Configuration for the analytics event buffer
 */
interface BufferConfig {
  maxBufferSize: number; // Max events before auto-flush
  flushIntervalMs: number; // Time interval for auto-flush
  maxRetries: number; // Max retry attempts for failed requests
  retryDelayMs: number; // Delay between retries
}

/**
 * Event priority levels
 */
export enum EventPriority {
  IMMEDIATE = "immediate", // Send immediately, bypass buffer
  BUFFERED = "buffered", // Add to buffer, send in batch
}

/**
 * Event types that should be sent immediately
 */
const IMMEDIATE_EVENT_TYPES = new Set(["purchase", "membership_trial_start"]);

/**
 * Singleton Analytics Event Buffer Manager
 * Batches events and sends them efficiently to reduce network overhead
 */
class AnalyticsEventBuffer {
  private static instance: AnalyticsEventBuffer | null = null;

  private buffer: AnalyticsEvent[] = [];
  private flushTimer: ReturnType<typeof setTimeout> | null = null;
  private isFlushing = false;
  private config: BufferConfig;
  private sentEventIds = new Set<string>(); // Track sent events to prevent duplicates

  private constructor(config?: Partial<BufferConfig>) {
    this.config = {
      maxBufferSize: config?.maxBufferSize ?? 10,
      flushIntervalMs: config?.flushIntervalMs ?? 5000, // 5 seconds
      maxRetries: config?.maxRetries ?? 3,
      retryDelayMs: config?.retryDelayMs ?? 1000,
    };

    // Set up page lifecycle listeners
    this.setupLifecycleListeners();

    devLog("📊 Analytics Event Buffer initialized with config:", this.config);
  }

  /**
   * Get the singleton instance
   */
  static getInstance(config?: Partial<BufferConfig>): AnalyticsEventBuffer {
    if (!AnalyticsEventBuffer.instance) {
      AnalyticsEventBuffer.instance = new AnalyticsEventBuffer(config);
    }
    return AnalyticsEventBuffer.instance;
  }

  /**
   * Reset the singleton instance (mainly for testing)
   */
  static resetInstance(): void {
    if (AnalyticsEventBuffer.instance) {
      AnalyticsEventBuffer.instance.cleanup();
      AnalyticsEventBuffer.instance = null;
    }
  }

  /**
   * Fast hash function for strings (FNV-1a algorithm)
   * Non-cryptographic hash - suitable for deduplication
   */
  private hashString(str: string): string {
    let hash = 2166136261; // FNV offset basis

    for (let i = 0; i < str.length; i++) {
      hash ^= str.charCodeAt(i);
      hash += (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24);
    }

    // Convert to positive hex string
    return (hash >>> 0).toString(36);
  }

  /**
   * Generate a unique ID for an event by hashing the entire event body
   */
  private generateEventId(event: AnalyticsEvent): string {
    // Stringify the event to get a stable string representation
    // Sort keys to ensure consistent ordering
    const eventString = JSON.stringify(event, Object.keys(event).sort());

    // Hash the stringified event
    return this.hashString(eventString);
  }

  /**
   * Add an event to the buffer or send immediately based on priority
   */
  async addEvent(event: AnalyticsEvent, priority?: EventPriority): Promise<void> {
    // Check for duplicate events
    const eventId = this.generateEventId(event);
    if (this.sentEventIds.has(eventId)) {
      devLog(`⏭️  Skipping duplicate event: ${event.event_type} (${eventId})`);
      return;
    }

    // Mark event as sent
    this.sentEventIds.add(eventId);

    const effectivePriority = priority ?? this.determineEventPriority(event);

    if (effectivePriority === EventPriority.IMMEDIATE) {
      devLog("⚡ Sending immediate event:", event.event_type);
      await this.sendEvents([event]);
      return;
    }

    // Add to buffer
    this.buffer.push(event);
    devLog(`📥 Event buffered: ${event.event_type} (buffer size: ${this.buffer.length})`);

    // Check if we need to flush based on buffer size
    if (this.buffer.length >= this.config.maxBufferSize) {
      devLog("📦 Buffer size limit reached, flushing...");
      await this.flush();
      return;
    }

    // Start or reset the flush timer
    this.resetFlushTimer();
  }

  /**
   * Determine event priority based on event type
   */
  private determineEventPriority(event: AnalyticsEvent): EventPriority {
    return IMMEDIATE_EVENT_TYPES.has(event.event_type) ? EventPriority.IMMEDIATE : EventPriority.BUFFERED;
  }

  /**
   * Manually flush all buffered events
   */
  async flush(): Promise<void> {
    // Clear the timer since we're flushing now
    this.clearFlushTimer();

    if (this.buffer.length === 0) {
      devLog("📭 No events to flush");
      return;
    }

    if (this.isFlushing) {
      devLog("⏳ Flush already in progress, skipping...");
      return;
    }

    this.isFlushing = true;

    // Take all events from buffer
    const eventsToSend = [...this.buffer];
    this.buffer = [];

    devLog(`📤 Flushing ${eventsToSend.length} events...`);

    try {
      await this.sendEvents(eventsToSend);
      devLog("✅ Events flushed successfully");
      // Clear sent event IDs after successful flush to allow new events
      this.sentEventIds.clear();
      devLog("🧹 Cleared sent event IDs");
    } catch (error) {
      devError("❌ Failed to flush events:", error);
      // Put failed events back in buffer
      this.buffer.push(...eventsToSend);
    } finally {
      this.isFlushing = false;
    }
  }

  /**
   * Send events to the backend with retry logic
   */
  private async sendEvents(events: AnalyticsEvent[]): Promise<void> {
    let lastError: Error | null = null;

    for (let attempt = 0; attempt < this.config.maxRetries; attempt++) {
      try {
        await postAnalyticsEvents(events);
        return; // Success!
      } catch (error) {
        lastError = error as Error;
        devError(`❌ Send attempt ${attempt + 1} failed:`, error);

        // Wait before retry (except on last attempt)
        if (attempt < this.config.maxRetries - 1) {
          await this.delay(this.config.retryDelayMs * (attempt + 1)); // Exponential backoff
        }
      }
    }

    // All retries failed
    throw lastError || new Error("Failed to send events after retries");
  }

  /**
   * Reset the flush timer
   */
  private resetFlushTimer(): void {
    this.clearFlushTimer();
    this.flushTimer = setTimeout(() => {
      devLog("⏰ Flush timer triggered");
      this.flush();
    }, this.config.flushIntervalMs);
  }

  /**
   * Clear the flush timer
   */
  private clearFlushTimer(): void {
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
  }

  /**
   * Set up page lifecycle listeners
   */
  private setupLifecycleListeners(): void {
    // Flush on page visibility change (user switches tabs)
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", () => {
        if (document.hidden) {
          devLog("👁️ Page hidden, flushing buffer...");
          this.flush();
        }
      });
    }

    // Flush on page unload using sendBeacon for reliability
    if (typeof window !== "undefined") {
      window.addEventListener("beforeunload", () => {
        devLog("🚪 Page unloading, flushing buffer...");
        this.flushWithBeacon();
      });

      // Also flush on pagehide (more reliable on mobile)
      window.addEventListener("pagehide", () => {
        devLog("📴 Page hiding, flushing buffer...");
        this.flushWithBeacon();
      });
    }
  }

  /**
   * Flush using sendBeacon for page unload scenarios
   * This is more reliable than fetch() during page unload
   */
  private flushWithBeacon(): void {
    if (this.buffer.length === 0) return;

    const events = [...this.buffer];
    this.buffer = [];

    // Try using sendBeacon if available
    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      try {
        const blob = new Blob([JSON.stringify({ events })], {
          type: "application/json",
        });

        // We need to construct the full API URL
        const baseUrl = import.meta.env.VITE_API_BASE_URL || window.location.origin;
        const url = `${baseUrl}/api/pubsub_algo/events`;

        const sent = navigator.sendBeacon(url, blob);
        if (sent) {
          devLog("📡 Events sent via sendBeacon");
          return;
        }
      } catch (error) {
        devError("❌ sendBeacon failed:", error);
      }
    }

    // Fallback to synchronous fetch if sendBeacon not available or failed
    // Note: This may not complete if page unloads quickly
    this.flush();
  }

  /**
   * Clean up resources
   */
  cleanup(): void {
    this.clearFlushTimer();
    // Final flush attempt
    if (this.buffer.length > 0) {
      this.flushWithBeacon();
    }
    // Clear sent event IDs
    this.sentEventIds.clear();
  }

  /**
   * Clear sent event IDs (useful for testing or manual reset)
   */
  clearSentEventIds(): void {
    this.sentEventIds.clear();
    devLog("🧹 Cleared sent event IDs");
  }

  /**
   * Get current buffer size (for debugging/monitoring)
   */
  getBufferSize(): number {
    return this.buffer.length;
  }

  /**
   * Update configuration
   */
  updateConfig(config: Partial<BufferConfig>): void {
    this.config = { ...this.config, ...config };
    devLog("📊 Analytics buffer config updated:", this.config);
  }

  /**
   * Utility: delay helper
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

// Export singleton instance getter
export const getAnalyticsBuffer = (config?: Partial<BufferConfig>) => AnalyticsEventBuffer.getInstance(config);

// Export for testing
export const resetAnalyticsBuffer = () => AnalyticsEventBuffer.resetInstance();
